// ===== ICMS-ST da proposta inteira =====
//
// Spec: docs/superpowers/specs/2026-10-06-icms-st-na-proposta.md (§4.3)
//
// `calcularItemST` (src/icms-st.js) faz a conta de UM item. Aqui se decide QUEM paga ST, de onde vêm NCM, origem, UF e tipo de
// venda, como o frete entra na base de cada item e qual é o status da proposta.
//
// O PRINCÍPIO que atravessa o arquivo (Kaio, 07/10): o orçamento segue o Nomus, mesmo quando o Nomus está errado -- "não posso
// passar um valor para o cliente e depois cobrar outro". E: zero na tela afirma que não há imposto; "a confirmar" admite que não
// sabemos. Zero só aparece quando o Nomus de fato não cobra (`sem_st`, `nao_incide`), sempre com o motivo.

import { dinheiro, somarDinheiro } from './dinheiro.js';
import { calcularItemST, resolverRegra, MOTIVO_TIPO_VENDA_SEM_ST } from './icms-st.js';
import { REGRAS_ICMS_ST, VERSAO_REGRAS } from './icms-st-regras.js';

// Padrão da NF-e (indIEDest), gravado em `clientes.tipo_contribuinte_icms`: 1 contribuinte · 2 isento de inscrição · 3 não
// contribuinte (o XML usa 9).
//
// Medido nas notas: dos itens com ST, 100% têm indIEDest = 1. Os 2 casos com isento (2) a contabilidade respondeu que são "Erro"
// (02/10). Então 2, 3 e 9 não pagam ST, e é uma afirmação verdadeira.
const SEM_ST = new Set([2, 3, 9]);
const CONTRIBUINTE = 1;
// As regras do Nomus existem para origem 0 e 1. Outra origem (2 a 8) não tem entrada aqui: "a confirmar", nunca "sem ST".
const ORIGENS_COM_REGRA = new Set(['0', '1']);

/**
 * Rateia frete + seguro + outras despesas entre os itens, em centavos inteiros.
 *
 * Os métodos são os do Nomus (`METODOS_RATEIO`, src/data.jsx): 0 não ratear, 1 igualmente, 2 de acordo com o valor bruto de cada
 * item (o padrão). O centavo que sobra vai para o ÚLTIMO item, para a soma dos rateios ser exatamente o total informado -- peso e
 * quantidade não entram.
 */
export function ratearAcessorios(valoresCheios, total, metodo = 2) {
  const n = valoresCheios.length;
  const centavos = Math.round((Number(total) || 0) * 100);
  if (!n || centavos <= 0 || Number(metodo) === 0) return valoresCheios.map(() => 0);

  const somaValores = valoresCheios.reduce((s, v) => s + (Number(v) || 0), 0);
  let partes;
  if (Number(metodo) === 1) {
    partes = valoresCheios.map(() => Math.floor(centavos / n));
  } else {
    if (!(somaValores > 0)) return valoresCheios.map(() => 0);
    partes = valoresCheios.map((v) => Math.floor(centavos * (Number(v) || 0) / somaValores));
  }
  partes[n - 1] += centavos - partes.reduce((s, p) => s + p, 0);
  return partes.map((c) => c / 100);
}

/**
 * O que vai em `propostas.icms_st_total`. "A confirmar" grava NULL, não zero: a coluna é "nunca calculado" e zero afirmaria "sem
 * imposto". `nao_incide` grava 0 (essa afirmação é verdadeira); `parcial` grava o que foi calculado.
 */
export function totalParaGravar(estimativa) {
  return estimativa.status === 'a_confirmar' ? null : estimativa.total;
}

/**
 * Algum item teria regra de ST, mas o tipo de venda (Corporativa...) faz o Nomus emitir a nota sem ST?
 * So chega a esse ponto destinatario CONTRIBUINTE (isento e sem cadastro saem antes), entao e exatamente o caso em que o
 * vendedor pode ter escolhido o tipo errado. NCM sem regra tem outro motivo e nao conta.
 */
export function vendaSemSTPorTipo(estimativa) {
  return (estimativa?.itens || []).some((i) => i.status === 'sem_st' && i.motivo === MOTIVO_TIPO_VENDA_SEM_ST);
}

const nomeDe = (it) => it.nome || it.codigo || '(sem nome)';
const pendente = (it, motivo) => ({ id: it._id ?? null, nome: nomeDe(it), status: 'a_confirmar', motivo });

/**
 * Estimativa de ICMS-ST de uma proposta.
 *
 * @param itens         [{ _id, nome, codigo, ncm, origemProduto, quantidade, precoUnitario }] -- preço CHEIO
 * @param cliente       { tipoContribuinte, uf, nome, razaoSocial } -- a UF é a do destinatário, não a da emissora; o nome serve
 *                      para as regras próprias de cliente (Casarão Lustres)
 * @param tipoVendaId   ID do tipo de venda no Nomus (35 Lojista, 48 Corporativa...): o Nomus só cobra ST em Lojista/Showroom
 * @returns { versaoRegras, status, total, pendentes, motivo?, itens }
 *   status: 'calculado' (todos os itens) | 'parcial' | 'a_confirmar' | 'nao_incide'
 *   total:  ICMS-ST + FCP-ST das linhas calculadas -- o que vai em `propostas.icms_st_total`
 */
export function estimarICMSST({
  itens = [], cliente = {}, tipoVendaId = null, frete = 0, seguro = 0, outrasDespesas = 0, metodoRateio = 2, regras = REGRAS_ICMS_ST,
} = {}) {
  const base = { versaoRegras: VERSAO_REGRAS };
  const lista = Array.isArray(itens) ? itens : [];

  if (!lista.length) return { ...base, status: 'a_confirmar', total: 0, pendentes: 0, motivo: 'a proposta ainda não tem itens', itens: [] };

  const tipo = cliente?.tipoContribuinte == null || cliente.tipoContribuinte === '' ? null : Number(cliente.tipoContribuinte);

  if (tipo !== null && SEM_ST.has(tipo)) {
    return {
      ...base, status: 'nao_incide', total: 0, pendentes: 0, motivo: 'destinatário não é contribuinte de ICMS',
      itens: lista.map((it) => ({ id: it._id ?? null, nome: nomeDe(it), status: 'nao_incide', valor: 0 })),
    };
  }

  if (tipo !== CONTRIBUINTE) {
    const motivo = 'o cadastro fiscal do cliente não informa se ele é contribuinte de ICMS';
    return { ...base, status: 'a_confirmar', total: 0, pendentes: lista.length, motivo, itens: lista.map((it) => pendente(it, motivo)) };
  }

  const uf = String(cliente?.uf || '').trim().toUpperCase();
  const cheios = lista.map((it) => dinheiro((Number(it.quantidade) || 0) * (Number(it.precoUnitario) || 0)));
  const acessorios = somarDinheiro([frete, seguro, outrasDespesas]);
  const rateio = ratearAcessorios(cheios, acessorios, metodoRateio);

  const calculados = lista.map((it, i) => {
    if (!uf) return pendente(it, 'o cadastro do cliente não informa o estado (UF)');
    const ncm = String(it.ncm || '').trim();
    if (!ncm) return pendente(it, 'o produto não tem NCM no cadastro');
    if (it.origemProduto == null || it.origemProduto === '') return pendente(it, 'o produto não tem origem no cadastro');
    const origem = String(it.origemProduto).trim();
    if (!ORIGENS_COM_REGRA.has(origem)) return pendente(it, `a origem ${origem} do produto não tem regra de ST parametrizada (só 0 e 1)`);

    const regra = resolverRegra(regras, { uf, ncm, origem, cliente: [cliente?.nome, cliente?.razaoSocial] });
    const r = calcularItemST({ valorProduto: cheios[i], acessorios: rateio[i], regra, ufDestino: uf, origemProduto: origem, tipoVendaId });
    if (r.status === 'a_confirmar') return pendente(it, r.motivo);
    if (r.status === 'sem_st') return { id: it._id ?? null, nome: nomeDe(it), status: 'sem_st', motivo: r.motivo, valor: 0 };

    return {
      id: it._id ?? null, nome: nomeDe(it), status: 'calculado',
      regra: {
        uf: regra.uf, ncm: regra.ncm, origem: regra.origem, nivel: regra.nivel, mva: regra.mva, aliquota: regra.aliquota, fcp: regra.fcp,
        ...(regra.deducao != null ? { deducao: regra.deducao } : {}),
        ...(regra.clientes ? { regraDeCliente: true } : {}),
        ...(regra.alerta ? { alerta: regra.alerta } : {}),
      },
      baseProp: r.baseProp, baseST: r.baseST, deducaoPct: r.deducaoPct, icmsSt: r.icmsSt, fcpSt: r.fcpSt, valor: r.valor,
    };
  });

  const comValor = calculados.filter((c) => c.status === 'calculado');
  const semSt = calculados.filter((c) => c.status === 'sem_st');
  const pendentes = calculados.length - comValor.length - semSt.length;
  const primeiroPendente = calculados.find((c) => c.status === 'a_confirmar')?.motivo;

  // Nenhum item paga ST (Corporativa, ou NCM sem regra): "não se aplica", com o motivo do Nomus.
  if (!pendentes && !comValor.length) {
    return { ...base, status: 'nao_incide', total: 0, pendentes: 0, motivo: semSt[0]?.motivo, itens: calculados };
  }

  const status = pendentes === 0 ? 'calculado' : comValor.length ? 'parcial' : 'a_confirmar';
  return {
    ...base, status, total: somarDinheiro(comValor.map((c) => c.valor)), pendentes,
    ...(primeiroPendente ? { motivo: primeiroPendente } : {}),
    itens: calculados,
  };
}
